import { isNotNil } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import { areEqual, Guid } from "client_legacy/ndaw/guid";
import {
	AvailableNotificationViewModel,
	NotificationKind,
	UserRequestNotificationKind
} from "client/Api/Notifications/notifications";
import { OrderedReadonlyArray } from "../ActivityFeed/store";
import {
	HashNavigationRouteActionKind,
	HashNavigationRouteMatch,
	RouteParameters,
	QueryParameters
} from "./routing";

export function getActivityNavigationAction(
	activityBatchId: Guid,
	activities: OrderedReadonlyArray<AvailableNotificationViewModel>,
	hadInitialLoad: boolean
): ActivityNavigationAction {
	if (!hadInitialLoad) {
		return noneAction();
	}

	const activity: AvailableNotificationViewModel | undefined = activities
		.find(a => areEqual(a.batchId, activityBatchId));

	if (!activity) {
		return noneAction();
	}

	switch (activity.kind) {
		case NotificationKind.Announcement:
			return redirectToHashAction(
				HashNavigationRouteActionKind.Announcement,
				{
					announcementId: activity.data.announcementId.toString()
				}
			);
		case NotificationKind.AnnouncementComment:
			return redirectToHashAction(
				HashNavigationRouteActionKind.AnnouncementComment,
				{
					announcementId: activity.data.announcementId.toString(),
					commentId: activity.data.commentId.toString()
				}
			);
		case NotificationKind.AnnouncementCommentReaction:
			return redirectToHashAction(
				HashNavigationRouteActionKind.AnnouncementComment,
				{
					announcementId: activity.data.announcementId.toString(),
					commentId: activity.data.commentId.toString()
				}
			);
		case NotificationKind.AnnouncementCommentMention:
			return redirectToHashAction(
				HashNavigationRouteActionKind.AnnouncementComment,
				{
					announcementId: activity.data.announcementId.toString(),
					commentId: activity.data.commentId.toString()
				}
			);
		case NotificationKind.AnnouncementReaction:
			return redirectToHashAction(
				HashNavigationRouteActionKind.Announcement,
				{
					announcementId: activity.data.announcementId.toString()
				}
			);
		case NotificationKind.CommunityPostComment:
			return redirectToHashAction(
				HashNavigationRouteActionKind.CommunityPostComment,
				{
					postId: activity.data.postId.toString(),
					commentId: activity.data.commentId.toString()
				}
			);
		case NotificationKind.CommunityPostCommentMention:
			return redirectToHashAction(
				HashNavigationRouteActionKind.CommunityPostComment,
				{
					postId: activity.data.postId.toString(),
					commentId: activity.data.commentId.toString()
				}
			);
		case NotificationKind.CommunityPost:
			return redirectToHashAction(
				HashNavigationRouteActionKind.CommunityPost,
				{
					postId: activity.data.postId.toString()
				}
			);
		case NotificationKind.CommunityPostCommentReaction:
			return redirectToHashAction(
				HashNavigationRouteActionKind.CommunityPostComment,
				{
					postId: activity.data.postId.toString(),
					commentId: activity.data.commentId.toString()
				}
			);
		case NotificationKind.CommunityPostReaction:
			return redirectToHashAction(
				HashNavigationRouteActionKind.CommunityPost,
				{
					postId: activity.data.postId.toString()
				}
			);
		case NotificationKind.Community:
		case NotificationKind.CommunityMembership:
			return redirectToHashAction(
				HashNavigationRouteActionKind.CommunityFeed,
				{
					communityId: activity.data.communityId.toString()
				}
			);
		case NotificationKind.KnowledgeItem:
			return redirectToHashAction(
				HashNavigationRouteActionKind.KnowledgeItem,
				{
					knowledgeItemId: activity.data.knowledgeItemId.toString(),
				}
			);
		case NotificationKind.Event:
			return redirectToHashAction(
				HashNavigationRouteActionKind.Event,
				{
					eventId: activity.data.eventId.toString(),
				}
			);
		case NotificationKind.EventAttendance:
			return redirectToHashAction(
				HashNavigationRouteActionKind.Event,
				{
					eventId: activity.data.eventId.toString(),
				}
			);
		case NotificationKind.AppAccess:
		case NotificationKind.NewMainAppPromotion:
			return redirectToHashAction(
				HashNavigationRouteActionKind.AppStore
			);
		case NotificationKind.Birthday:
			return {
				kind: ActivityNavigationActionKind.OpenBirthdayWishDialog,
				userId: activity.data.userId,
				firstName: activity.data.firstname,
				lastName: activity.data.lastname
			};
		case NotificationKind.DocumentShared:
			return redirectToHashAction(
				HashNavigationRouteActionKind.OpenDocument,
				{},
				{
					siteUrl: activity.data.location.siteUrl,
					listId: activity.data.location.listId,
					itemId: activity.data.documentItemId.toString(10)
				}
			);
		case NotificationKind.NewEmail:
			return {
				kind: ActivityNavigationActionKind.OpenEmail,
				emailId: activity.data.exchangeId
			};
		case NotificationKind.UserRequest:
			if (isNotNil(activity.data.requestId)) {
				switch (activity.data.kind) {
					case UserRequestNotificationKind.Requested:
						return redirectToHashAction(
							HashNavigationRouteActionKind.UserRequestDetails,
							{
								requestId: activity.data.requestId.toString(10)
							}
						);
					case UserRequestNotificationKind.Responded:
						return redirectToHashAction(
							HashNavigationRouteActionKind.UserRequestResponseDetails,
							{
								requestId: activity.data.requestId.toString(10)
							}
						);
					default:
						return never(activity.data);
				}
			} else {
				return openActivityFeedAction();
			}
		case NotificationKind.EventComment:
			return redirectToHashAction(
				HashNavigationRouteActionKind.EventComment,
				{
					eventId: activity.data.eventId.toString(),
					commentId: activity.data.commentId.toString()
				}
			);
		case NotificationKind.EventCommentReaction:
			return redirectToHashAction(
				HashNavigationRouteActionKind.EventComment,
				{
					eventId: activity.data.eventId.toString(),
					commentId: activity.data.commentId.toString()
				}
			);
		case NotificationKind.EventCommentMention:
			return redirectToHashAction(
				HashNavigationRouteActionKind.EventComment,
				{
					eventId: activity.data.eventId.toString(),
					commentId: activity.data.commentId.toString()
				}
			);
		case NotificationKind.EventReaction:
			return redirectToHashAction(
				HashNavigationRouteActionKind.Event,
				{
					eventId: activity.data.eventId.toString()
				}
			);
		case NotificationKind.KnowledgeItemComment:
			return redirectToHashAction(
				HashNavigationRouteActionKind.KnowledgeItemComment,
				{
					knowledgeItemId: activity.data.knowledgeItemId.toString(),
					commentId: activity.data.commentId.toString()
				}
			);
		case NotificationKind.KnowledgeItemCommentReaction:
			return redirectToHashAction(
				HashNavigationRouteActionKind.KnowledgeItemComment,
				{
					knowledgeItemId: activity.data.knowledgeItemId.toString(),
					commentId: activity.data.commentId.toString()
				}
			);
		case NotificationKind.KnowledgeItemCommentMention:
			return redirectToHashAction(
				HashNavigationRouteActionKind.KnowledgeItemComment,
				{
					knowledgeItemId: activity.data.knowledgeItemId.toString(),
					commentId: activity.data.commentId.toString()
				}
			);
		case NotificationKind.KnowledgeItemReaction:
			return redirectToHashAction(
				HashNavigationRouteActionKind.KnowledgeItem,
				{
					knowledgeItemId: activity.data.knowledgeItemId.toString()
				}
			);
		case NotificationKind.BirthdayWish:
		case NotificationKind.BirthdayWishReaction:
		case NotificationKind.CrossStoreOperation:
		case NotificationKind.Custom:
		case NotificationKind.Maintenance:
		case NotificationKind.Unknown:
			return openActivityFeedAction();
		default:
			return never(activity);
	}
}

export type ActivityNavigationAction = {
	kind: ActivityNavigationActionKind.None;
} | {
	kind: ActivityNavigationActionKind.OpenActivityFeed;
} | {
	kind: ActivityNavigationActionKind.RedirectHash;
	hashMatch: HashNavigationRouteMatch;
} | {
	kind: ActivityNavigationActionKind.OpenEmail;
	emailId: string;
} | {
	kind: ActivityNavigationActionKind.OpenBirthdayWishDialog;
	userId: number;
	firstName: string;
	lastName: string;
};

export enum ActivityNavigationActionKind {
	None = 0,
	OpenActivityFeed = 1,
	RedirectHash = 2,
	OpenEmail = 3,
	OpenBirthdayWishDialog = 4
}

function redirectToHashAction<TKind extends HashNavigationRouteActionKind>(
	action: TKind,
	params: RouteParameters<TKind> = {},
	queryParams: QueryParameters<TKind> = {}
): ActivityNavigationAction {
	return {
		kind: ActivityNavigationActionKind.RedirectHash,
		hashMatch: {
			action,
			params,
			queryParams
		}
	};
}

function noneAction(): ActivityNavigationAction {
	return {
		kind: ActivityNavigationActionKind.None
	};
}

function openActivityFeedAction(): ActivityNavigationAction {
	return {
		kind: ActivityNavigationActionKind.OpenActivityFeed
	};
}